import React from "react";
import "./Appbar.scss";

import { Link } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import PhoneIcon from "@mui/icons-material/Phone";

// import Stack from '@mui/material/Stack';

import navLogo from "../../assets/carp_house_plain.png";
import { scrollToHeroFormAndFocusName } from "../../utils/scrollToForm";
import { navCtaSx } from "../../styles/ctaStyles";

export const PHONE_NUMBER = "317-812-1388";
export const PHONE_HREF = "tel:+13178121388";

const NAV_LINKS = [
  { label: "Sell My Home", to: "/sell-my-home" },
  { label: "About", to: "/about" },
  { label: "Seller Stories", to: "/seller-stories" },
  { label: "Available Homes", to: "/available-homes" },
  { label: "FAQ's", to: "/frequently-asked-questions" },
  { label: "Contact", to: "/contact" },
];

const Appbar = (props) => {
  const [anchorElNav, setAnchorElNav] = React.useState(null);

  const callLinkSx = {
    color: "inherit",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    fontFamily: "latoBold",
    whiteSpace: "nowrap",
  };

  // The header CTA always targets the *hero* form (the first #lead-form on
  // the page), and puts the cursor in the Name field.
  const scrollToHeroForm = () => {
    window.gtag?.("event", "cta_click", {
      event_category: "CTA",
      event_label: "Header Get My Offer",
    });
    scrollToHeroFormAndFocusName();
  };

  // Ad traffic hides nav links and hamburger
  const isAd = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const search = window.location.search || "";
    return search.includes("gclid") || search.includes("utm_medium=cpc");
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    // No wrapper element: `position: sticky` is relative to the nearest
    // scrolling ancestor, but the element can only stick *within its own
    // parent's box*. Wrapping the bar in a short <div> made it scroll away
    // with the page instead of staying pinned to the top.
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        zIndex: 1300,
        backgroundColor: props.theme.palette.primary.main,
        backgroundImage: "none",
      }}
    >
        <Container maxWidth="none">
          <Toolbar disableGutters sx={{ gap: 1, minHeight: { xs: 60, md: 64 } }}>
            {/* ---------- MOBILE ---------- */}
            {!isAd && (
              <IconButton
                size="large"
                aria-label="Open navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
                sx={{ display: { xs: "inline-flex", md: "none" }, mr: 0.5 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center" }}>
              <img className="navLogo" src={navLogo} alt="CarpHomes Logo" width="40" height="38" />
            </Box>

            {/* Mobile phone button: icon + "Call" */}
            <Box
              component="a"
              href={PHONE_HREF}
              aria-label={`Call ${PHONE_NUMBER}`}
              onClick={() =>
                window.gtag?.("event", "call_click", {
                  event_category: "Contact",
                  event_label: "Header Call Button",
                })
              }
              sx={{
                ...callLinkSx,
                display: { xs: "inline-flex", md: "none" },
                ml: "auto",
                px: 1,
              }}
            >
              <PhoneIcon fontSize="small" />
              <span>Call</span>
            </Box>

            {/* Mobile CTA */}
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={scrollToHeroForm}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                ...navCtaSx,
                fontSize: "0.8rem",
                px: 1.5,
              }}
            >
              Get My Offer
            </Button>

            {/* ---------- DESKTOP ---------- */}
            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
              <img className="navLogo" src={navLogo} alt="CarpHomes Logo" width="40" height="38" />
            </Box>

            <Typography
              noWrap
              component="a"
              href="/"
              sx={{
                mr: 2,
                display: { xs: "none", md: "flex" },
                color: props.theme.palette.common.white,
                letterSpacing: ".25rem",
                textDecoration: "none",
              }}
            >
              <span style={{ textTransform: "none", fontWeight: 700 }}>
                Carp
              </span>
              <span
                style={{
                  color: props.theme.palette.warning.main,
                  fontFamily: "latoBold",
                }}
              >
                Homes
              </span>
            </Typography>

            {!isAd && (
              <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                {NAV_LINKS.map((item) => (
                  <MenuItem key={item.to} component={Link} to={item.to}>
                    <Typography
                      sx={[
                        { color: props.theme.palette.common.white },
                        {
                          ":hover": {
                            color: props.theme.palette.warning.main,
                          },
                        },
                      ]}
                    >
                      {item.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Box>
            )}

            {isAd && <Box sx={{ flexGrow: 1 }} />}

            {/* Desktop phone button */}
            <Box
              component="a"
              href={PHONE_HREF}
              aria-label={`Call ${PHONE_NUMBER}`}
              onClick={() =>
                window.gtag?.("event", "call_click", {
                  event_category: "Contact",
                  event_label: "Header Call Button",
                })
              }
              sx={{
                ...callLinkSx,
                display: { xs: "none", md: "inline-flex" },
                mr: 1.5,
              }}
            >
              <PhoneIcon fontSize="small" />
              <span>Call {PHONE_NUMBER}</span>
            </Box>

            {/* Desktop CTA */}
            <Button
              variant="contained"
              color="warning"
              onClick={scrollToHeroForm}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                ...navCtaSx,
              }}
            >
              Get My Offer
            </Button>

            {/* Mobile nav menu */}
            {!isAd && (
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{ display: { xs: "block", md: "none" } }}
              >
                {NAV_LINKS.map((item) => (
                  <MenuItem
                    key={item.to}
                    component={Link}
                    to={item.to}
                    onClick={handleCloseNavMenu}
                  >
                    <span style={{ color: "#1a2038" }}>{item.label}</span>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Toolbar>
        </Container>
    </AppBar>
  );
};

export default Appbar;